import { createSlice, createAction } from '@reduxjs/toolkit';

export const initiatePayment = createAction('INITIATE_PAYMENT');
export const listPlans = createAction('LIST_PLANS');
export const getBillingDetails = createAction('GET_BILLING_DETAILS');
export const createSubscription = createAction('CREATE_SUBSCRIPTION');
export const saveBillingDetails = createAction('SAVE_BILLING_DETAILS');
export const getSubscriptionStatus = createAction('GET_SUBSCRIPTION_STATUS');
export const cancelPayment = createAction('CANCEL_PAYMENT');

const initialState = {
  paymentData: null,
  paymentFlow: null,
  loading: false,
  error: null,
  success: false,
  plans: [],
  billingTabs: [],
  loadingPlans: false,
  errorPlans: null,
  billingDetails: null,
  loadingBillingDetails: false,
  errorBillingDetails: null,
  savingBillingDetails: false,
  errorSavingBillingDetails: null,
  subscription: null,
  loadingSubscription: false,
  errorSubscription: null,
  errorSubscriptionStatus: null,
  cancelingPayment: false,
  cancelPaymentSuccess: null,
  cancelPaymentError: null,
  paymentSuccessData: null,
  showPaymentSuccessModal: false,
};

const billingSlice = createSlice({
  name: 'billing',
  initialState,
  reducers: {
    initiatePaymentRequest: (state, action) => {
      state.loading = true;
      state.error = null;
      state.success = false;
      state.paymentFlow = action.payload?.paymentFlow ?? 'subscription';
    },
    initiatePaymentSuccess: (state, action) => {
      state.loading = false;
      state.paymentData = action.payload;
      state.success = true;
    },
    initiatePaymentFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.success = false;
    },
    resetBillingState: (state) => {
      state.paymentData = null;
      state.paymentFlow = null;
      state.loading = false;
      state.error = null;
      state.success = false;
    },
    listPlansRequest: (state) => {
      state.loadingPlans = true;
      state.errorPlans = null;
    },
    listPlansSuccess: (state, action) => {
      state.loadingPlans = false;
      state.plans = action.payload?.plans || [];
      state.billingTabs = action.payload?.billingTabs || [];
    },
    listPlansFailure: (state, action) => {
      state.loadingPlans = false;
      state.errorPlans = action.payload;
    },
    getBillingDetailsRequest: (state) => {
      state.loadingBillingDetails = true;
      state.errorBillingDetails = null;
    },
    getBillingDetailsSuccess: (state, action) => {
      state.loadingBillingDetails = false;
      state.billingDetails = action.payload;
    },
    getBillingDetailsFailure: (state, action) => {
      state.loadingBillingDetails = false;
      state.errorBillingDetails = action.payload;
    },
    createSubscriptionRequest: (state) => {
      state.loadingSubscription = true;
      state.errorSubscription = null;
    },
    createSubscriptionSuccess: (state, action) => {
      state.loadingSubscription = false;
      state.subscription = action.payload;
    },
    createSubscriptionFailure: (state, action) => {
      state.loadingSubscription = false;
      state.errorSubscription = action.payload;
    },
    resetSubscriptionState: (state) => {
      state.subscription = null;
      state.loadingSubscription = false;
      state.errorSubscription = null;
    },
    saveBillingDetailsRequest: (state) => {
      state.savingBillingDetails = true;
      state.errorSavingBillingDetails = null;
    },
    saveBillingDetailsSuccess: (state, action) => {
      state.savingBillingDetails = false;
      if (action.payload) {
        state.billingDetails = { ...state.billingDetails, ...action.payload };
      }
    },
    saveBillingDetailsFailure: (state, action) => {
      state.savingBillingDetails = false;
      state.errorSavingBillingDetails = action.payload;
    },
    getSubscriptionStatusRequest: (state) => {
      state.loadingSubscription = true;
      state.errorSubscriptionStatus = null;
    },
    getSubscriptionStatusSuccess: (state, action) => {
      state.loadingSubscription = false;
      state.errorSubscriptionStatus = null;
      state.subscription = action.payload;
    },
    getSubscriptionStatusFailure: (state, action) => {
      state.loadingSubscription = false;
      state.errorSubscriptionStatus = action.payload;
    },
    cancelPaymentRequest: (state) => {
      state.cancelingPayment = true;
      state.cancelPaymentError = null;
      state.cancelPaymentSuccess = null;
    },
    cancelPaymentSuccess: (state, action) => {
      state.cancelingPayment = false;
      state.cancelPaymentSuccess = action.payload;
      state.cancelPaymentError = null;
    },
    cancelPaymentFailure: (state, action) => {
      state.cancelingPayment = false;
      state.cancelPaymentError = action.payload;
      state.cancelPaymentSuccess = null;
    },
    setPaymentSuccessData: (state, action) => {
      state.paymentSuccessData = action.payload;
      state.showPaymentSuccessModal = true;
    },
    closePaymentSuccessModal: (state) => {
      state.showPaymentSuccessModal = false;
      state.paymentSuccessData = null;
    },
  },
});

export const {
  initiatePaymentRequest,
  initiatePaymentSuccess,
  initiatePaymentFailure,
  resetBillingState,
  listPlansRequest,
  listPlansSuccess,
  listPlansFailure,
  getBillingDetailsRequest,
  getBillingDetailsSuccess,
  getBillingDetailsFailure,
  createSubscriptionRequest,
  createSubscriptionSuccess,
  createSubscriptionFailure,
  resetSubscriptionState,
  saveBillingDetailsRequest,
  saveBillingDetailsSuccess,
  saveBillingDetailsFailure,
  getSubscriptionStatusRequest,
  getSubscriptionStatusSuccess,
  getSubscriptionStatusFailure,
  cancelPaymentRequest,
  cancelPaymentSuccess,
  cancelPaymentFailure,
  setPaymentSuccessData,
  closePaymentSuccessModal,
} = billingSlice.actions;

export default billingSlice.reducer;